import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    FaDownload, FaFilePdf, FaCheckCircle, FaCloud, FaCode,
    FaMobileAlt, FaDraftingCompass, FaShoppingCart, FaBullhorn,
    FaStar, FaRocket, FaShieldAlt, FaUsers,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { generateBrochurePDF } from '../utils/brochureGenerator';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './Brochure.css';

const BROCHURE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Company Brochure | Nimai Solutions",
    "description": "Download the Nimai Solutions company brochure — services, portfolio highlights, and contact information in one PDF.",
    "url": `${SITE_URL}/brochure`,
};

const PAGES_PREVIEW = [
    {
        icon: <FaFilePdf />,
        title: 'Cover Page',
        desc: 'Company name, tagline, branding, and at-a-glance contact details.',
        color: '#FF9F1C',
    },
    {
        icon: <FaStar />,
        title: 'About Us',
        desc: 'Mission, vision, values, and what makes Nimai Solutions different.',
        color: '#3B82F6',
    },
    {
        icon: <FaCode />,
        title: 'Our Services',
        desc: 'Full breakdown of all digital services we provide, with descriptions.',
        color: '#10B981',
    },
    {
        icon: <FaRocket />,
        title: 'Stats & Process',
        desc: 'Key metrics, experience stats, and our step-by-step delivery process.',
        color: '#8B5CF6',
    },
    {
        icon: <FaUsers />,
        title: 'Contact Us',
        desc: 'Email, phone, address, website, and a call-to-action for new projects.',
        color: '#F43F5E',
    },
];

const serviceIconMap = {
    cloud:  <FaCloud />,
    code:   <FaCode />,
    mobile: <FaMobileAlt />,
    design: <FaDraftingCompass />,
    shop:   <FaShoppingCart />,
    marketing: <FaBullhorn />,
};

const Brochure = () => {
    useSEO({
        title:       'Company Brochure | Nimai Solutions',
        description: 'Download the Nimai Solutions company brochure — a professional PDF covering services, stats, process, and contact info.',
        canonical:   `${SITE_URL}/brochure`,
        keywords:    'Nimai Solutions brochure, company profile download, digital agency profile PDF Vadodara',
        schema:      BROCHURE_SCHEMA,
    });

    const [services,  setServices]  = useState([]);
    const [stats,     setStats]     = useState([]);
    const [config,    setConfig]    = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const [svcRes, statRes, cfgRes] = await Promise.all([
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/services'),
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/experience-stats'),
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/brochure-config')
                        .catch(() => ({ ok: false, data: null })),
                ]);

                if (svcRes.ok) {
                    const d = svcRes.data?.data || svcRes.data;
                    setServices(Array.isArray(d) ? d : []);
                }
                if (statRes.ok) {
                    const d = statRes.data?.data || statRes.data;
                    setStats(Array.isArray(d) ? d : Object.values(d || {}));
                }
                if (cfgRes.ok && cfgRes.data) {
                    setConfig(cfgRes.data?.data || cfgRes.data || {});
                }
            } catch (_) { /* silently fall back to defaults */ }
        };
        load();
    }, []);

    const handleDownload = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            await generateBrochurePDF(config, services, stats);
            setDownloaded(true);
            setTimeout(() => setDownloaded(false), 4000);
        } catch (err) {
            console.error('Brochure generation error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const getIcon = (icon) => serviceIconMap[icon?.toLowerCase()] || <FaCode />;

    const displayServices = services.length > 0 ? services : [
        { id: 1, title: 'Web Development',   icon: 'code',   description: 'React, Next.js & Node.js applications built for performance and scale.' },
        { id: 2, title: 'Mobile Apps',        icon: 'mobile', description: 'Flutter & React Native apps for iOS and Android.' },
        { id: 3, title: 'UI/UX Design',       icon: 'design', description: 'Human-centric interfaces that convert visitors into customers.' },
        { id: 4, title: 'Cloud Platforms',    icon: 'cloud',  description: 'AWS, Azure & GCP infrastructure management.' },
        { id: 5, title: 'E-Commerce',         icon: 'shop',   description: 'Full-featured online stores with payment integration.' },
        { id: 6, title: 'Digital Marketing',  icon: 'marketing', description: 'SEO, SEM, and content strategies that grow your reach.' },
    ];

    return (
        <div className="brochure-page">
            <Navbar />

            {/* Hero */}
            <section className="brochure-hero">
                <motion.div
                    className="brochure-hero-bg-circle"
                    animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                    className="brochure-hero-content"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="brochure-eyebrow">FREE DOWNLOAD</span>
                    <h1 className="brochure-hero-title">
                        Our <span className="brochure-highlight">Company</span><br />Brochure
                    </h1>
                    <p className="brochure-hero-desc">
                        A professionally designed 5-page PDF covering everything about Nimai Solutions —
                        services, stats, process, and how to reach us. Share it with your team or clients.
                    </p>

                    <motion.button
                        className={`brochure-dl-btn ${isLoading ? 'loading' : ''} ${downloaded ? 'success' : ''}`}
                        onClick={handleDownload}
                        whileHover={!isLoading ? { scale: 1.04 } : {}}
                        whileTap={!isLoading ? { scale: 0.97 } : {}}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <><span className="btn-spinner" /> Generating PDF…</>
                        ) : downloaded ? (
                            <><FaCheckCircle /> Downloaded!</>
                        ) : (
                            <><FaDownload /> Download Brochure (PDF)</>
                        )}
                    </motion.button>

                    <p className="brochure-meta">
                        <FaFilePdf className="pdf-icon" /> 5 pages &nbsp;·&nbsp; A4 format &nbsp;·&nbsp; ~200 KB &nbsp;·&nbsp; Always up-to-date
                    </p>
                </motion.div>

                {/* Brochure mockup */}
                <motion.div
                    className="brochure-mockup"
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.9, delay: 0.3 }}
                >
                    <motion.div
                        className="mock-pdf-card"
                        animate={{ y: [-8, 8, -8] }}
                        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        <div className="mock-pdf-top">
                            <div className="mock-logo-row">
                                <div className="mock-logo-circle" />
                                <div>
                                    <div className="mock-line thick orange" style={{ width: 80 }} />
                                    <div className="mock-line thin" style={{ width: 55 }} />
                                </div>
                            </div>
                        </div>
                        <div className="mock-pdf-body">
                            <div className="mock-headline">
                                <div className="mock-line thick" style={{ width: '70%' }} />
                                <div className="mock-line orange" style={{ width: '55%' }} />
                                <div className="mock-line thin" style={{ width: '60%' }} />
                            </div>
                            <div className="mock-badge-row">
                                {['Web Dev', 'Mobile', 'UI/UX', 'Cloud'].map((t) => (
                                    <span key={t} className="mock-badge">{t}</span>
                                ))}
                            </div>
                        </div>
                        <div className="mock-pdf-footer">
                            <div className="mock-line thin" style={{ width: '50%' }} />
                            <div className="mock-line orange" style={{ width: '30%' }} />
                        </div>
                        <div className="mock-pdf-page-tag">PDF · 5 Pages</div>
                    </motion.div>
                </motion.div>
            </section>

            {/* What's Inside */}
            <section className="brochure-inside">
                <motion.div
                    className="section-head"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="section-eyebrow">WHAT'S INSIDE</span>
                    <h2>5 Pages of Pure Value</h2>
                    <p>Every page is designed to be informative, professional, and shareable.</p>
                </motion.div>

                <div className="pages-grid">
                    {PAGES_PREVIEW.map((page, i) => (
                        <motion.div
                            key={i}
                            className="page-preview-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1, duration: 0.5 }}
                            whileHover={{ y: -6, transition: { duration: 0.25 } }}
                        >
                            <div className="page-num" style={{ color: page.color }}>0{i + 1}</div>
                            <div className="page-icon" style={{ color: page.color, backgroundColor: page.color + '18' }}>
                                {page.icon}
                            </div>
                            <h3>{page.title}</h3>
                            <p>{page.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Services Preview */}
            <section className="brochure-services-preview">
                <motion.div
                    className="section-head"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="section-eyebrow">SERVICES INCLUDED</span>
                    <h2>What We Build For You</h2>
                    <p>All services listed below are documented in the brochure's Services page.</p>
                </motion.div>

                <div className="services-preview-grid">
                    {displayServices.slice(0, 6).map((svc, i) => (
                        <motion.div
                            key={svc.id || i}
                            className="svc-preview-card"
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                            whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
                        >
                            <div className="svc-icon">{getIcon(svc.icon)}</div>
                            <h4>{svc.title}</h4>
                            <p>{svc.description}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Qualities / Trust */}
            <section className="brochure-trust">
                {[
                    { icon: <FaShieldAlt />, title: 'Always Current',  desc: 'Generated live from our latest data — every download is up-to-date.' },
                    { icon: <FaFilePdf />,   title: 'Print-Ready PDF', desc: 'High-quality A4 layout suitable for printing or digital sharing.' },
                    { icon: <FaDownload />,  title: 'Instant Download', desc: 'No registration required. Click, generate, and download in seconds.' },
                    { icon: <FaCheckCircle />, title: 'Shareable',     desc: 'Perfect for proposals, emails, meetings, and client presentations.' },
                ].map((item, i) => (
                    <motion.div
                        key={i}
                        className="trust-item"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                    >
                        <div className="trust-icon">{item.icon}</div>
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                    </motion.div>
                ))}
            </section>

            {/* Bottom CTA */}
            <section className="brochure-cta">
                <motion.div
                    className="brochure-cta-inner"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="section-eyebrow" style={{ color: '#FF9F1C' }}>READY?</span>
                    <h2>Get Your Copy Now</h2>
                    <p>It's free, instant, and packed with everything you need to know about us.</p>

                    <motion.button
                        className={`brochure-dl-btn large ${isLoading ? 'loading' : ''} ${downloaded ? 'success' : ''}`}
                        onClick={handleDownload}
                        whileHover={!isLoading ? { scale: 1.05 } : {}}
                        whileTap={!isLoading ? { scale: 0.97 } : {}}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <><span className="btn-spinner" /> Building your PDF…</>
                        ) : downloaded ? (
                            <><FaCheckCircle /> Enjoy Your Brochure!</>
                        ) : (
                            <><FaDownload /> Download Company Brochure</>
                        )}
                    </motion.button>
                </motion.div>
            </section>

            <Footer />
        </div>
    );
};

export default Brochure;
